import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Business,
  Security,
  Speed,
  SupportAgent,
  CheckCircle,
  ArrowForward,
  Cloud,
  Devices,
  Public,
  Analytics,
  Router,
  CellTower,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const EnterpriseSolutions = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Consistent stats matching the correct data
  const stats = [
    { number: '50,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '100+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '15+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const solutions = [
    { icon: <Cloud />, title: 'Global MPLS Network', desc: 'Connect all your offices worldwide with private, secure connectivity' },
    { icon: <Devices />, title: 'Unified Communications', desc: 'Voice, video, and messaging integrated into one platform' },
    { icon: <Public />, title: 'IoT Connectivity', desc: 'Global SIM cards and device management platform' },
    { icon: <Analytics />, title: 'SD-WAN Services', desc: 'Intelligent traffic routing and optimized cloud connectivity' },
  ];

  const benefits = [
    'Dedicated account team with 24/7 enterprise support',
    'Custom SLAs tailored to your business needs',
    'Priority network routing for mission-critical applications',
    'Advanced DDoS protection and threat monitoring',
    'Compliance-ready solutions (GDPR, HIPAA, PCI-DSS)',
    'Flexible contract terms and pricing models',
  ];

  return (
    <Box>
      <PageHeader 
        title="Enterprise Solutions" 
        subtitle="Custom communication infrastructure for global enterprises"
      />

      {/* Stats Section with Future Goal Note */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Single Future Goal Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
              <Typography variant="body1" sx={{ color: '#191919' }}>
                <Box component="span" sx={{ color: '#e60108', fontWeight: 700 }}>🎯 Future Goal:</Box> These statistics represent our ambitious targets for the coming years as we continue to expand and enhance our global communication infrastructure.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Solutions Grid */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Enterprise Solutions" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Tailored Solutions for Global Enterprises
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  We understand that every enterprise has unique requirements. Our team works 
                  closely with you to design, deploy, and manage a communication infrastructure 
                  that scales with your business.
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {solutions.map((solution, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Avatar sx={{ bgcolor: '#e6010815', color: '#e60108', width: 40, height: 40 }}>
                          {solution.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#191919' }}>
                            {solution.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                            {solution.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1551434678-e076c2236a9b?w=600&h=400&fit=crop"
                  alt="Enterprise Solutions"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Why Choose Us" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Why Choose Our Enterprise Solutions?
                </Typography>
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#e60108' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit} 
                        primaryTypographyProps={{ sx: { color: '#191919' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card sx={{ p: 4, bgcolor: '#e60108', color: '#fefefe', borderRadius: 4 }}>
                  <Typography variant="h4" gutterBottom fontWeight="800">
                    Ready to Transform Your Enterprise?
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                    Schedule a consultation with our enterprise team to discuss your specific requirements.
                  </Typography>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={() => navigate('/contact')}
                      sx={{
                        bgcolor: '#fefefe',
                        color: '#e60108',
                        fontWeight: 700,
                        '&:hover': { 
                          bgcolor: '#fefefe', 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      Contact Enterprise Team
                    </Button>
                  </motion.div>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Transform Your Enterprise" 
        subtitle="Get a custom solution designed for your global operations"
        buttonText="Request Consultation"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default EnterpriseSolutions;